// status-progress-journal-facts.ts
//
// Progress panel P1: the time/session facts `payload.progress` needs, folded
// from the SAME lock-free journal records the rest of the progress block
// reads (one scan — N7). Pure: records in, facts out. Old records carry no
// `recorded_at`/`sessionId` ⇒ the matching field stays absent, never guessed.

import type { JournalRecord } from "./journal-scan-report.js";
import { ATTEMPT_USAGE_RECORDED_KIND } from "./mission-cost-accounting-types.js";
import type { ProgressAttempt } from "./status-progress-types.js";

export interface ProgressAttemptFact extends ProgressAttempt {
  ticket: string;
  seq: number;
  finished: boolean;
}

export interface TicketTimeFacts {
  started_at?: string;
  /** recorded_at of the last ticket_state_changed — the reader keeps it only for a closed status. */
  last_change_at?: string;
}

export interface ProgressJournalFacts {
  missionStartedAt?: string;
  attemptsByTicket: Map<string, ProgressAttemptFact[]>;
  costByTicket: Map<string, number>;
  timesByTicket: Map<string, TicketTimeFacts>;
  /** Seq of the last `mission_status_changed` (0 when none). */
  lastStatusLabelSeq: number;
  /** Seq of the last attempt_started / ticket_state_changed (0 when none). */
  lastWorkSeq: number;
}

const str = (v: unknown): string | undefined => (typeof v === "string" ? v : undefined);
const num = (v: unknown): number | undefined =>
  typeof v === "number" && Number.isFinite(v) ? v : undefined;

function timesOf(facts: ProgressJournalFacts, ticket: string): TicketTimeFacts {
  const existing = facts.timesByTicket.get(ticket);
  if (existing !== undefined) return existing;
  const fresh: TicketTimeFacts = {};
  facts.timesByTicket.set(ticket, fresh);
  return fresh;
}

function onAttemptStarted(facts: ProgressJournalFacts, record: JournalRecord, ticket: string) {
  const { delta, seq } = record;
  const at = str(record.recorded_at);
  const attempt: ProgressAttemptFact = {
    ticket,
    seq,
    finished: false,
    attempt_no: num(delta.attemptNo) ?? 0,
    run_id: str(delta.runId) ?? "",
    ...(str(delta.sessionId) === undefined ? {} : { session_id: str(delta.sessionId) }),
    ...(at === undefined ? {} : { started_at: at }),
  };
  facts.attemptsByTicket.set(ticket, [...(facts.attemptsByTicket.get(ticket) ?? []), attempt]);
  const times = timesOf(facts, ticket);
  if (times.started_at === undefined && at !== undefined) times.started_at = at;
  facts.lastWorkSeq = seq;
}

function onAttemptFinished(facts: ProgressJournalFacts, record: JournalRecord, ticket: string) {
  const attemptNo = num(record.delta.attemptNo);
  const open = [...(facts.attemptsByTicket.get(ticket) ?? [])]
    .reverse()
    .find((a) => !a.finished && (attemptNo === undefined || a.attempt_no === attemptNo));
  if (open === undefined) return;
  open.finished = true;
  const at = str(record.recorded_at);
  const category = str(record.delta.category);
  if (at !== undefined) open.finished_at = at;
  if (category !== undefined) open.category = category;
}

/** One pass over the records, in seq order. */
export function progressJournalFacts(records: readonly JournalRecord[]): ProgressJournalFacts {
  const facts: ProgressJournalFacts = {
    attemptsByTicket: new Map(),
    costByTicket: new Map(),
    timesByTicket: new Map(),
    lastStatusLabelSeq: 0,
    lastWorkSeq: 0,
  };
  const first = records[0];
  if (first !== undefined && str(first.recorded_at) !== undefined) {
    facts.missionStartedAt = str(first.recorded_at);
  }
  for (const record of records) {
    const { delta, seq } = record;
    const ticket = str(delta.ticket);
    if (delta.kind === "mission_status_changed") facts.lastStatusLabelSeq = seq;
    if (ticket === undefined) continue;
    if (delta.kind === "attempt_started") onAttemptStarted(facts, record, ticket);
    else if (delta.kind === "attempt_finished") onAttemptFinished(facts, record, ticket);
    else if (delta.kind === "ticket_state_changed") {
      const at = str(record.recorded_at);
      const times = timesOf(facts, ticket);
      if (at === undefined) delete times.last_change_at;
      else times.last_change_at = at;
      facts.lastWorkSeq = seq;
    } else if (delta.kind === ATTEMPT_USAGE_RECORDED_KIND) {
      const cost = num(delta.costUsd);
      if (cost !== undefined)
        facts.costByTicket.set(ticket, (facts.costByTicket.get(ticket) ?? 0) + cost);
    }
  }
  return facts;
}

/** The open attempt (no attempt_finished with its attemptNo) of `ticket`, if its LAST attempt is open. */
export function openAttemptOf(
  facts: ProgressJournalFacts,
  ticket: string,
): ProgressAttemptFact | undefined {
  const last = facts.attemptsByTicket.get(ticket)?.at(-1);
  return last !== undefined && !last.finished ? last : undefined;
}

export function toProgressAttempt(fact: ProgressAttemptFact): ProgressAttempt {
  const { ticket: _t, seq: _s, finished: _f, ...attempt } = fact;
  return attempt;
}
